import { CustomerContactTypeEnum } from '../gql/customerGql';
import { Deal } from '../gql/dealGql';
import { Deal as GeneratedDeal, Notification } from '../gql/generated/graphql';

enum NotificationType {
  NOTE_CREATED = 'NOTE_CREATED',
  DEAL_STATE_UPDATE = 'DEAL_STATE_UPDATE',
  MESSAGE_RECEIVED = 'MESSAGE_RECEIVED', // Used for notification toasts for texts, and everything in the text widget
  DEAL_SEEN = 'DEAL_SEEN',
  CALL_RECEIVED = 'CALL_RECEIVED',
  CALL_MADE = 'CALL_MADE',
  CALL_RINGING = 'CALL_RINGING',
}

export type PersonIdFieldMapKey =
  typeof CustomerContactTypeEnum[keyof typeof CustomerContactTypeEnum];

export const RecipientTypeMap: Record<PersonIdFieldMapKey, string> = {
  [CustomerContactTypeEnum.Customer]: 'customer',
  [CustomerContactTypeEnum.Cobuyer]: 'cobuyer',
  [CustomerContactTypeEnum.Contact]: 'contact',
  [CustomerContactTypeEnum.Second]: 'secondContact',
  [CustomerContactTypeEnum.OriginalLessee]: 'originalLessee',
};

type NotificationMessageMap =
  | NotificationType.NOTE_CREATED
  | NotificationType.DEAL_STATE_UPDATE
  | NotificationType.MESSAGE_RECEIVED
  | NotificationType.CALL_RECEIVED
  | NotificationType.CALL_MADE
  | NotificationType.CALL_RINGING;

export const notificationMessageMap: Record<NotificationMessageMap, string> = {
  [NotificationType.NOTE_CREATED]: 'Note Added',
  [NotificationType.DEAL_STATE_UPDATE]: 'Deal State Updated',
  [NotificationType.MESSAGE_RECEIVED]: 'Text Message Received',
  [NotificationType.CALL_RECEIVED]: 'Call Recieved',
  [NotificationType.CALL_MADE]: 'Call Made',
  [NotificationType.CALL_RINGING]: 'Call Ringing',
};

// Same logic as `filterByUnseenMessages` modifier in API (types/notification.ts). Keep in sync.
export const isMessageUnseen = (
  userId: string,
  input: Notification | Notification[] | null | undefined,
  userOnly = false,
) => {
  // `input` can be an array because a text message can belong to multiple deals. So it will have a different notification for each deal.
  // In this context, it's already filtered to the current deal, so it will contain only one element.

  const messageNotifications = Array.isArray(input)
    ? input.filter((n) => n.message === notificationMessageMap.MESSAGE_RECEIVED)
    : input?.message === notificationMessageMap.MESSAGE_RECEIVED
    ? [input]
    : [];

  if (messageNotifications.length === 0) {
    return false;
  }

  const allMessageNotificationUsers = messageNotifications.flatMap((n) => n.users || []);
  const userMessageNotifications = allMessageNotificationUsers.filter(
    (nu) => nu?.user_id === userId,
  );

  if (userMessageNotifications.length > 0) {
    // Should be exactly 1 notification for this user.
    // `.every` is used for robustness in case of unexpected multiples.

    // A) User does have a row. He hasn't seen it.
    return userMessageNotifications.every((nu) => nu?.seen_at === null);
  }

  if (userOnly) {
    return false;
  }

  if (allMessageNotificationUsers.length === 0) {
    return false;
  }

  // B) User does NOT have a notification user. Nobody has seen it.
  return allMessageNotificationUsers.every((nu) => nu?.seen_at === null);
};

type DealWithNotifications = Pick<Deal, 'notifications'> | Pick<GeneratedDeal, 'notifications'>;

export const getUnseenMessageNotificationsCount = (
  userId: string,
  input?: DealWithNotifications | DealWithNotifications[] | null,
  userOnly = false,
): number => {
  if (!input) {
    return 0;
  }

  const notificationsArray = Array.isArray(input)
    ? input.flatMap((deal) => deal.notifications ?? [])
    : input.notifications ?? [];

  return notificationsArray.reduce(
    (count, n) => (isMessageUnseen(userId, n, userOnly) ? count + 1 : count),
    0,
  );
};
